import { Spinner } from '@ui';
import { cx } from 'class-variance-authority';
import { useClubSlug } from 'components/Clubs/hooks/useClubSlug';
import { VoucherCard } from 'components/SpaceBooking/types';
import { VoucherCardComponent } from 'components/SpaceBooking/VoucherCard/VoucherCardComponent';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { AppState } from '../../../../redux/store';

interface Props {
    email: string;
    usedUnits?: number;
    clubSlug?: string;
}

export const VoucherCardAssistent = ({ email: _email, clubSlug: _clubSlug, usedUnits }: Props) => {
    const { setValue } = useFormContext();
    const user = useSelector((state: AppState) => state.auth.user);
    const email = _email ?? user?.email;
    const [activeVoucher, setActiveVoucher] = useState<VoucherCard>();
    const [voucherValue, setVoucherValue] = useState<number>();
    const clubSlug = useClubSlug(_clubSlug);

    const { data, error } = useSWR<{ data: VoucherCard[] }>(
        email && email.length > 4 && clubSlug ? `space/club/${clubSlug}/voucher/${decodeURIComponent(email)}` : null
    );
    const isLoading = !data && !error;

    const checkUnitSize = (voucher: VoucherCard): boolean => {
        return usedUnits % voucher.unit_size === 0;
    };

    const calculateNewValue = (voucher: VoucherCard): number => {
        return usedUnits / voucher.unit_size + voucher.value;
    };

    const selectVoucher = (voucher: VoucherCard) => {
        if (activeVoucher === voucher) {
            setActiveVoucher(null);
        } else {
            setActiveVoucher(voucher);
        }
    };

    useEffect(() => {
        setValue('space_voucher_card_id', activeVoucher ? activeVoucher.id : null);
        setValue('space_voucher_card_value', activeVoucher ? voucherValue : null);
    }, [voucherValue, activeVoucher]);

    return email ? (
        isLoading ? (
            <Spinner />
        ) : (
            <div className="tw-my-4 tw-flex tw-flex-col">
                {data?.data?.map((voucher) => (
                    <div
                        key={voucher.id}
                        className={cx(
                            'tw-card-bordered',
                            checkUnitSize(voucher) && 'tw-card-clickable',
                            activeVoucher === voucher && 'tw-selected'
                        )}
                        onClick={() => checkUnitSize(voucher) && selectVoucher(voucher)}
                    >
                        <VoucherCardComponent
                            small
                            voucher={voucher}
                            callback={setVoucherValue}
                            newValue={activeVoucher === voucher && calculateNewValue(voucher)}
                            disabled={!checkUnitSize(voucher) || activeVoucher !== voucher}
                        />
                        {!checkUnitSize(voucher) && (
                            <small className="tw-mt-4 tw-pl-1 tw-text-error">
                                Die von dir eingestellte Einheitengröße lässt sich nicht mit der Länge dieser Buchung
                                vereinbaren.
                            </small>
                        )}
                    </div>
                ))}
            </div>
        )
    ) : null;
};
